/**
 * Iter 45p — Add Progress Entry dialog.
 *
 * Lightweight entry surface for logging goal evidence without the
 * live data tracker. Three entry types:
 *   - Narrative observation  (NEVER plots on the chart)
 *   - Percent / Trials       (plots when n + d are provided)
 *   - Informal Assessment    (plots when n + d are provided)
 *
 * Used from the Goal Progress page. Saves to /api/goal-evidence
 * with source = "manual_progress_entry".
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { localDateStr } from "@/lib/utils";
import api from "@/lib/api";

const ENTRY_TYPES = [
  {
    value: "narrative_observation",
    label: "Narrative Observation",
    blurb: "Free-text observation. Will appear in the evidence timeline only — does not plot on the chart.",
  },
  {
    value: "percent_trials",
    label: "Percent / Trials",
    blurb: "Provide correct/total. Plots on the chart with source label \"Manual Entry\".",
  },
  {
    value: "informal_assessment",
    label: "Informal Assessment / Probe",
    blurb: "Probe-style score with optional label. Plots on the chart with source label \"Informal Assessment\".",
  },
];

export default function AddProgressEntryDialog({ open, onOpenChange, goal, studentId, providerId, onSaved }) {
  const [entryType, setEntryType] = useState("narrative_observation");
  const [date, setDate] = useState(localDateStr());
  const [narrative, setNarrative] = useState("");
  const [numerator, setNumerator] = useState("");
  const [denominator, setDenominator] = useState("");
  const [assessmentLabel, setAssessmentLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setEntryType("narrative_observation");
      setDate(localDateStr());
      setNarrative("");
      setNumerator("");
      setDenominator("");
      setAssessmentLabel("");
    }
  }, [open]);

  const meta = ENTRY_TYPES.find((t) => t.value === entryType) || ENTRY_TYPES[0];
  const showRatio = entryType === "percent_trials" || entryType === "informal_assessment";

  const handleSubmit = async () => {
    if (!goal || !studentId || !providerId) return;
    setSubmitting(true);
    try {
      const payload = {
        student_id: studentId,
        provider_id: providerId,
        goal_id: goal.id,
        date,
        entry_type: entryType,
        source: "manual_progress_entry",
        narrative_note: narrative,
        assessment_label: entryType === "informal_assessment" ? assessmentLabel : "",
      };
      if (showRatio) {
        const n = parseInt(numerator, 10);
        const d = parseInt(denominator, 10);
        if (Number.isFinite(n)) payload.numerator = n;
        if (Number.isFinite(d)) payload.denominator = d;
      }
      await api.createGoalEvidence(payload);
      toast.success("Progress entry saved");
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      console.error(err);
      toast.error("Could not save entry — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (!goal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md bg-[#FBFAF6]"
        data-testid="add-progress-entry-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#1F2937] heading-font">
            Add Progress Entry
          </DialogTitle>
          <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{goal.goal_text}</p>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs font-medium text-[#1F2937] mb-1 block">Entry Type</Label>
            <Select value={entryType} onValueChange={setEntryType}>
              <SelectTrigger data-testid="add-entry-type" className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTRY_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#6B7280] mt-1">{meta.blurb}</p>
          </div>

          <div>
            <Label htmlFor="add-entry-date" className="text-xs font-medium text-[#1F2937] mb-1 block">
              Date
            </Label>
            <Input
              id="add-entry-date"
              data-testid="add-entry-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="text-sm"
            />
          </div>

          {showRatio && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">Correct</Label>
                <Input
                  data-testid="add-entry-numerator"
                  type="number"
                  min="0"
                  value={numerator}
                  onChange={(e) => setNumerator(e.target.value)}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">Total</Label>
                <Input
                  data-testid="add-entry-denominator"
                  type="number"
                  min="0"
                  value={denominator}
                  onChange={(e) => setDenominator(e.target.value)}
                  className="text-sm"
                />
              </div>
            </div>
          )}

          {entryType === "informal_assessment" && (
            <div>
              <Label className="text-xs font-medium text-[#1F2937] mb-1 block">
                Probe / Assessment Label (optional)
              </Label>
              <Input
                data-testid="add-entry-assessment-label"
                value={assessmentLabel}
                onChange={(e) => setAssessmentLabel(e.target.value)}
                placeholder="e.g. Monthly Writing Probe"
                className="text-sm"
              />
            </div>
          )}

          <div>
            <Label className="text-xs font-medium text-[#1F2937] mb-1 block">
              {entryType === "narrative_observation" ? "Observation" : "Note (optional)"}
            </Label>
            <Textarea
              data-testid="add-entry-note"
              value={narrative}
              onChange={(e) => setNarrative(e.target.value)}
              placeholder={
                entryType === "narrative_observation"
                  ? "What did you notice today?"
                  : "Optional context for this data point"
              }
              className="min-h-[100px] text-sm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="add-entry-submit"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            {submitting ? "Saving..." : "Save Entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
